import type { PaginationProps } from "@mantine/core";
import { Button, Modal, Pagination } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usersMapper } from "src/components/pages/Summary/VerifyButton";
import AuthorizationModal from "src/generator/_Modals/AuthorizationModal/AuthorizationModal";
import { refreshLogoutTimer } from "src/generator/utils/refreshLogoutTimer";
import { useGetApi, usePostApi } from "src/hooks";
import type { ICredentials } from "src/interfaces";
import { Flex } from "src/styled";
import { Notification } from "src/utils/Notification";

import ClearSelectionsButton from "../EntriesSelectList/ClearSelectionsButton";
import EntriesSelectList from "../EntriesSelectList/EntriesSelectList";
import SelectAllButton from "../EntriesSelectList/SelectAllButton";
import type { IEntrySelectValue, MultipleModalProps } from "../Multiple.model";

const MultipleVerifyModal = ({ open, close, refetch }: MultipleModalProps) => {
  const { post, isLoading } = usePostApi();
  const [selectedEntries, setSelectedEntries] = useState<IEntrySelectValue[]>(
    [],
  );
  const [items, setItems] = useState<any[]>([]);
  const [page, setPage] = useState<number>(1);
  const [saveModalOpened, { open: openSaveModal, close: closeSaveModal }] =
    useDisclosure(false);

  const handleClose = (modalCloseFn: () => void) => {
    setSelectedEntries([]);
    modalCloseFn();
  };

  const setAllSelected = useCallback(
    (entries: any) => {
      setSelectedEntries(selected => [
        ...selected,
        ...(
          entries?.map(
            (
              item: Partial<{ lbeid: string; meid: string; partid: string }>,
            ) => {
              const type = item.meid
                ? "meid"
                : item.partid
                  ? "partid"
                  : item.lbeid
                    ? "lbeid"
                    : "";

              return {
                idType: type,
                id: item[type as keyof typeof item] ?? "",
              };
            },
          ) ?? []
        ).filter(e => !selected.find(se => se.id === e.id)),
      ]);
    },
    [setSelectedEntries],
  );

  const clearAllSelected = useCallback(
    (entries: any) => {
      setSelectedEntries(selected => [
        ...selected.filter(
          se =>
            !entries.find(
              (e: Partial<{ lbeid: string; meid: string; partid: string }>) => {
                const type = e.meid
                  ? "meid"
                  : e.partid
                    ? "partid"
                    : e.lbeid
                      ? "lbeid"
                      : "";

                return se.id === e[type as keyof typeof e];
              },
            ),
        ),
      ]);
    },
    [setSelectedEntries],
  );

  const pageSize = 20;
  const indexOffset = useMemo(() => (page - 1) * pageSize, [page]);

  const toggleCollapse = useCallback(
    (index: number) => {
      const target = index + indexOffset;
      setItems((prevItems: any[]) =>
        prevItems.map((item, i) =>
          i === target ? { ...item, collapsed: !item.collapsed } : item,
        ),
      );
    },
    [indexOffset],
  );

  const {
    data,
    refetch: fetchEntries,
    isLoading: loadingEntries,
  } = useGetApi({
    request: "get-summary-required-list",
    fetchOnInit: false,
    mapper: r => ({
      entries:
        r.data
          // .filter(e => e.lbentry?.sections?.length > 0)
          .map((e: any) => e.lbentry?.sections || []) || [],
      items:
        r.data
          // .filter(e => e.lbentry?.sections?.length > 0)
          .map((e: any) => {
            delete e.lbentry;
            return { ...e, collapsed: true };
          }) || [],
      users: r.users || [],
    }),
  });
  useEffect(() => {
    if (open && !data) {
      fetchEntries().catch(() => {
        Notification(
          "Error",
          "Failed to fetch entries. Please try again later.",
          "red",
        );
      });
    }
  }, [open, data, fetchEntries]);

  useEffect(() => {
    setItems(data?.items || []);
  }, [data?.items]);

  const handleOpenSaveModal = useCallback(async () => {
    openSaveModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [openSaveModal, close]);

  const handleVerify = useCallback(
    async ({ userid: user, ...credentials }: ICredentials) => {
      const list = selectedEntries.map(e => ({
        [e.idType]: e.id,
      }));
      await post({
        request: "verify-entry",
        body: { params: { ...credentials, user, list } },
        onSuccess: () => {
          handleClose(closeSaveModal);
          Notification(
            "Success",
            "Selected entries have been verified",
            "green",
          );
          refetch();
          fetchEntries().catch(() => {
            Notification(
              "Error",
              "Failed to fetch updated entries. Please refresh the page.",
              "red",
            );
          });
          refreshLogoutTimer();
        },
      });
    },
    [post, selectedEntries, refetch, closeSaveModal, fetchEntries],
  );

  const paginationProps: PaginationProps = useMemo(
    () => ({
      total: Math.ceil(data?.entries?.length / pageSize) || 1,
      value: page || 1,
      onChange: setPage,
    }),
    [data?.entries.length, page],
  );

  const pageItems = useMemo(() => {
    return items.slice(indexOffset, indexOffset + pageSize);
  }, [items, indexOffset]);

  const pageEntries = useMemo(() => {
    return data?.entries.slice(indexOffset, indexOffset + pageSize) || [];
  }, [data?.entries, indexOffset, pageSize]);

  return (
    <>
      <Modal
        title="Verify multiple entries"
        opened={open}
        onClose={() => handleClose(close)}
        withCloseButton={true}
        closeButtonProps={{ size: "md" }}
        zIndex={11}
        size="100%"
        styles={{ body: { maxHeight: "80vh", overflow: "hidden" } }}>
        <Flex justify="space-between" align="center">
          <Pagination {...paginationProps} />
          <Flex h={30} justify="end" gap={8} pr={12}>
            <ClearSelectionsButton
              setItems={clearAllSelected}
              items={pageItems}
            />
            <SelectAllButton setItems={setAllSelected} items={pageItems} />
          </Flex>
        </Flex>
        <Flex direction="column" mt={6}>
          <EntriesSelectList
            loading={loadingEntries}
            items={pageItems}
            toggleCollapse={toggleCollapse}
            entries={pageEntries}
            selectedEntries={selectedEntries}
            setSelectedEntries={setSelectedEntries}
          />
        </Flex>
        <Flex justify="end" mt={12} pr={12}>
          <Button
            variant="outline"
            onClick={handleOpenSaveModal}
            disabled={selectedEntries.length === 0}
            loading={false}>
            Verify selected
          </Button>
        </Flex>
      </Modal>
      <AuthorizationModal
        opened={saveModalOpened}
        close={() => handleClose(closeSaveModal)}
        onSubmit={handleVerify}
        loading={isLoading}
        users={usersMapper({ list: data?.users || [] })}
      />
    </>
  );
};
export default MultipleVerifyModal;
